import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAccounts } from '../../context/AccountContext';
import ScalingModal from '../../components/ScalingModal';

export default function MyTraders() {
  const { scopeParams, accounts } = useAccounts();
  const multiAccount = accounts.length > 1;
  const [rels, setRels]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg]         = useState('');
  const [error, setError]     = useState('');

  function load() {
    setLoading(true);
    api.get('/copier/my-traders', { params: scopeParams })
      .then(r => setRels(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load your traders'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [scopeParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mutations address the RELATIONSHIP id: with multi-account a copier can
  // follow one trader from several of their own accounts, so a trader id no
  // longer identifies a single relationship.
  //
  // Wrapped so a failure surfaces to the user instead of dying as an unhandled
  // rejection with no feedback.
  async function mutate(relId, fn, okMsg) {
    setError('');
    if (!relId) { setError('Relationship reference missing — reload the page and retry.'); return; }
    try {
      await fn();
      setMsg(okMsg);
      load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  const pause  = (relId) => mutate(relId,
    () => api.put(`/copier/relationships/${relId}`, { status: 'paused' }), 'Relationship paused.');
  const resume = (relId) => mutate(relId,
    () => api.put(`/copier/relationships/${relId}`, { status: 'active' }), 'Relationship resumed.');
  const unfollow = (relId) => {
    if (!window.confirm('Unfollow this trader?')) return;
    return mutate(relId, () => api.delete(`/copier/relationships/${relId}`), 'Unfollowed.');
  };

  return (
    <div className="row">
      {editing && (
        <ScalingModal
          trader={editing.trader}
          existing={editing}
          onClose={() => setEditing(null)}
          onSave={() => { setEditing(null); load(); }}
        />
      )}
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title">My Traders ({rels.length})</h5>
          </div>
          <div className="card-body">
            {msg && <div className="alert alert-info alert-dismissible">{msg}
              <button className="close" onClick={() => setMsg('')}>&times;</button></div>}
            {error && <div className="alert alert-danger alert-dismissible">{error}
              <button className="close" onClick={() => setError('')}>&times;</button></div>}
            {loading ? <p className="text-muted">Loading...</p> : (
              rels.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">You are not following any traders.</p>
                  <a href="/copier/find" className="btn btn-primary">Find Traders</a>
                </div>
              ) : (
                <div className="table-responsive table-cards table-cards-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Trader</th>
                        {multiAccount && <th>Copying into</th>}
                        <th>Scaling</th>
                        <th className="num">Risk Mult</th>
                        <th>Lot Range</th>
                        <th>Status</th>
                        <th className="num">Copied</th>
                        <th className="num">P&L</th>
                        <th>Last Trade</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rels.map(r => (
                        <tr key={r.id}>
                          <td data-label="Trader">
                            <div>
                              {r.trader?.username || r.trader?.email}
                              {r.trader?.accountLabel ? ` · ${r.trader.accountLabel}` : ''}
                            </div>
                            <div className="text-muted small">{r.trader?.mt5Server}</div>
                          </td>
                          {multiAccount && (
                            <td data-label="Copying into" className="small">
                              {r.copierAccountLabel || '—'}
                            </td>
                          )}
                          <td data-label="Scaling"><span className="badge badge-info">{r.scalingMode}</span></td>
                          <td data-label="Risk Mult" className="num">{r.riskMultiplier}x</td>
                          <td data-label="Lot Range" className="small">
                            {r.minLotSize} – {r.maxLotSize > 0 ? r.maxLotSize : 'no cap'}</td>
                          <td data-label="Status">
                            <span className={`badge badge-${r.status === 'active' ? 'success' : 'warning'}`}>{r.status}</span>
                          </td>
                          <td data-label="Copied" className="num">{r.totalCopiedTrades}</td>
                          <td data-label="P&L" className={`num ${(Number(r.totalPnl) || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                            {(Number(r.totalPnl) || 0) >= 0 ? '+' : ''}{(Number(r.totalPnl) || 0).toFixed(2)}
                          </td>
                          <td data-label="Last Trade" className="text-muted small">
                            {r.lastActivity ? new Date(r.lastActivity).toLocaleString() : '—'}
                          </td>
                          <td data-label="Actions">
                            <button className="btn btn-info btn-sm mr-1" onClick={() => setEditing({ ...r, trader: r.trader })}>
                              Edit
                            </button>
                            {r.status === 'active' ? (
                              <button className="btn btn-warning btn-sm mr-1" onClick={() => pause(r.id)}>Pause</button>
                            ) : (
                              <button className="btn btn-success btn-sm mr-1" onClick={() => resume(r.id)}>Resume</button>
                            )}
                            <button className="btn btn-danger btn-sm" onClick={() => unfollow(r.id)}>Unfollow</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
